import { ComponentProps, Fragment } from 'react';
import dynamic from 'next/dynamic';
import { TFunction } from 'i18next';
import {
  getServerLocale,
  initServerTranslation,
} from '@bts-web/utils-lokalise';
import { getTransactionDetailsTranslations } from './utils';
import { isCryptoTransferTransaction } from '../transactions/types/transactions';
import { WithServerSpanHOC } from '@bts-web/utils-next-infrastructure';

const TransactionDetailsSkeleton = dynamic(
  () =>
    import('./TransactionDetailsSkeleton/TransactionDetailsSkeleton').then(
      (mod) => mod.TransactionDetailsSkeleton,
    ),
  {
    ssr: true,
    loading: () => <Fragment />,
  },
);

const TransactionDetailsLayout = dynamic(
  () =>
    import('./TransactionDetailsLayout/TransactionDetailsLayout').then(
      (mod) => mod.TransactionDetailsLayout,
    ),
  {
    ssr: true,
    loading: () => <Fragment />,
  },
);

const CryptoTransferDetailsContent = dynamic(
  () =>
    import(
      '../crypto-transfer-details/CryptoTransferDetailsContent/CryptoTransferDetailsContent'
    ).then((mod) => mod.CryptoTransferDetailsContent),
  {
    ssr: true,
    loading: () => <TransactionDetailsSkeleton />,
  },
);

const TransactionDetailsContent = dynamic(
  () =>
    import('./TransactionDetailsContent/TransactionDetailsContent').then(
      (mod) => mod.TransactionDetailsContent,
    ),
  {
    ssr: true,
    loading: () => <TransactionDetailsSkeleton />,
  },
);

export type TransactionDetailsControllerProps = {
  transactionId: string;
  transactionType: ComponentProps<
    typeof TransactionDetailsContent
  >['transactionType'];
};

async function TransactionDetailsController({
  transactionId,
  transactionType,
}: TransactionDetailsControllerProps) {
  const { t } = await initServerTranslation();

  const locale = await getServerLocale();

  const translations = getTransactionDetailsTranslations(t as TFunction);

  const isCryptoTransaction = isCryptoTransferTransaction(transactionType);

  return (
    <TransactionDetailsLayout navigationBarTitle={translations.topBar}>
      {isCryptoTransaction ? (
        <CryptoTransferDetailsContent
          cryptoTransferId={transactionId}
          locale={locale}
        />
      ) : (
        <TransactionDetailsContent
          transactionId={transactionId}
          locale={locale}
          transactionType={transactionType}
        />
      )}
    </TransactionDetailsLayout>
  );
}

const TransactionDetailsControllerWithSpan = WithServerSpanHOC(
  'transaction.details.render',
  TransactionDetailsController,
);

export { TransactionDetailsControllerWithSpan as TransactionDetailsController };
